// pages/Contact.jsx
// Purpose: a quick, reliable contact form with good user experience.
// It calls trigger flows existing API at /api/contact.
// We add validation, loading state, and success/error feedback.

import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { track } from "@vercel/analytics";
import styles from "./Contact.module.css";
import { API_BASE, warmUpApi } from "../lib/api";

const CAL_BOOKING_URL = "https://cal.com/triggerflow/30min";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [status, setStatus] = useState({ state: "idle", note: "" });
  // state: idle | sending | success | error

  // Reaching this page is a strong signal the visitor may submit, so wake the
  // backend now to avoid a cold-start delay on send.
  useEffect(() => {
    warmUpApi();
  }, []);

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function submit(e) {
    e.preventDefault();

    // simple validation before sending
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ state: "error", note: "Please fill in all fields." });
      return;
    }

    setStatus({ state: "sending", note: "Sending your message…" });

    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, email: form.email, message: form.message, website: form.website })
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Request failed");
      }

      setStatus({ state: "success", note: "Thanks — we'll reply within 1 business day." });
      track("contact_submit");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setStatus({ state: "error", note: "Sorry—something went wrong. Please try again." });
      console.error(err);
    }
  }

  return (
    <section className="section" id="contact-form">
      <Helmet>
        <title>Contact — TriggerFlow</title>
        <meta name="description" content="Get in touch with TriggerFlow. We reply within 1 business day. No obligation — describe what you need and we'll suggest the clearest next step." />
      </Helmet>

      <h1 className="section__title">Contact</h1>
      <p className={styles.hint}>
        Tell us what you are trying to build, fix or improve. If you are not sure what you
        need yet, describe the problem and we will suggest the simplest route.
      </p>

      {/* What to expect */}
      <div className={styles.expectations}>
        <p className={styles.expectLabel}>What to expect</p>
        <ul className={styles.expectList}>
          <li>We reply within 1 business day.</li>
          <li>No obligation — just an honest conversation.</li>
          <li>We will ask a few questions, then suggest the clearest next step.</li>
        </ul>
        <p className={styles.expectBooking}>
          Prefer to book a call directly?{" "}
          <a
            href={CAL_BOOKING_URL}
            className={styles.calLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            Book a free 30-minute call {/* PLACEHOLDER: Cal.com link */}
          </a>
        </p>
      </div>

      <form className="form" onSubmit={submit} noValidate>
        <label className="form__label">
          Your name
          <input
            className="form__input"
            type="text"
            value={form.name}
            onChange={e => update("name", e.target.value)}
            placeholder="Your name"
            required
          />
        </label>

        <label className="form__label">
          Your email
          <input
            className="form__input"
            type="email"
            value={form.email}
            onChange={e => update("email", e.target.value)}
            placeholder="you@example.com"
            required
          />
        </label>

        <label className="form__label">
          Your message
          <textarea
            className="form__textarea"
            rows="6"
            value={form.message}
            onChange={e => update("message", e.target.value)}
            placeholder="What do you need?"
            required
          />
        </label>

        {/* Honeypot — hidden from real users, bots fill it in */}
        <input
          type="text"
          name="website"
          value={form.website}
          onChange={e => update("website", e.target.value)}
          style={{ display: "none" }}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />

        <button className="btn btn--primary" type="submit" disabled={status.state === "sending"}>
          {status.state === "sending" ? "Sending…" : "Send"}
        </button>

        {/* Feedback to the user */}
        {status.state === "success" && <p className="note note--success">{status.note}</p>}
        {status.state === "error" && <p className="note note--error">{status.note}</p>}

      </form>
    </section>
  );
}
